<template>
  <ContentWrap>
    <el-alert
      title="主播佣金 = 商品实付金额 × 商品佣金比例 × 主播分成比例"
      description="每个商品、每位主播分别设置，未设置按 0% 计算。例如：商品 20% × 主播 50% = 实际佣金 10%，商品实付 100 元时佣金为 10 元。新订单使用下单时保存的比例，修改设置对后续订单生效。"
      type="info"
      :closable="false"
      show-icon
      class="mb-16px"
    />
    <el-tabs v-model="tab" @tab-change="changeTab">
      <el-tab-pane label="商品佣金比例" name="product" />
      <el-tab-pane label="主播分成比例" name="anchor" />
    </el-tabs>
    <el-form v-if="tab === 'product'" inline @submit.prevent="search">
      <el-form-item label="商品名称">
        <el-input
          v-model="keyword"
          placeholder="搜索本店直播商品"
          clearable
          @keyup.enter="search"
        />
      </el-form-item>
      <el-form-item><el-button @click="search">搜索</el-button></el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="rows" stripe>
      <el-table-column
        :label="tab === 'product' ? '商品编号' : '主播编号'"
        prop="targetId"
        width="120"
      />
      <el-table-column
        :label="tab === 'product' ? '商品名称' : '主播昵称'"
        prop="name"
        min-width="200"
      />
      <el-table-column v-if="tab === 'product'" label="售价（元）" width="130">
        <template #default="{ row }">{{ ((row.price ?? 0) / 100).toFixed(2) }}</template>
      </el-table-column>
      <el-table-column :label="tab === 'product' ? '商品佣金比例' : '主播分成比例'" min-width="150">
        <template #default="{ row }">
          <span v-if="row.ratePercent != null">{{ row.ratePercent.toFixed(2) }}%</span>
          <el-tag v-else type="info">未设置（0%）</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="130" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openForm(row)">设置比例</el-button>
        </template>
      </el-table-column>
      <template #empty>
        {{
          tab === 'product'
            ? '暂无直播商品，请先在直播商品库导入本店商品'
            : '暂无审核通过的关联主播'
        }}
      </template>
    </el-table>
    <Pagination v-model:page="pageNo" v-model:limit="pageSize" :total="total" @pagination="load" />
  </ContentWrap>
  <el-dialog
    v-model="visible"
    :title="editing?.targetType === 1 ? '设置商品佣金比例' : '设置主播分成比例'"
    width="480px"
    @closed="formRef?.clearValidate()"
  >
    <el-form ref="formRef" :model="form" :rules="rules" label-width="120px" @submit.prevent="save">
      <el-form-item :label="editing?.targetType === 1 ? '商品' : '主播'"
        >{{ editing?.name }}（{{ editing?.targetId }}）</el-form-item
      >
      <el-form-item label="比例（%）" prop="ratePercent">
        <el-input-number
          v-model="form.ratePercent"
          :min="0"
          :max="100"
          :precision="2"
          :controls="false"
          class="!w-full"
          aria-label="佣金比例百分数"
        />
      </el-form-item>
      <el-form-item>可设置 0—100%，设置为 0% 后该对象不产生佣金。</el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="saving" @click="visible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="save">保存</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import type { FormInstance, FormRules } from 'element-plus'
import * as LiveApi from '@/api/merchant/live'

defineOptions({ name: 'MerchantLiveCommission' })
interface CommissionRow {
  targetId: number
  targetType: 1 | 2
  name: string
  price?: number
  ratePercent?: number
}
const message = useMessage()
const tab = ref('product')
const keyword = ref('')
const pageNo = ref(1)
const pageSize = ref(10)
const total = ref(0)
const rows = ref<CommissionRow[]>([])
const loading = ref(false)
const visible = ref(false)
const saving = ref(false)
const editing = ref<CommissionRow>()
const formRef = ref<FormInstance>()
const form = reactive<{ ratePercent: number | undefined }>({ ratePercent: undefined })
const rules: FormRules = {
  ratePercent: [{ required: true, message: '请输入 0—100% 的比例', trigger: 'blur' }]
}
let loadSequence = 0
const load = async () => {
  const sequence = ++loadSequence
  loading.value = true
  const targetType = tab.value === 'product' ? 1 : 2
  try {
    const [page, rates] = await Promise.all([
      targetType === 1
        ? LiveApi.getLiveProductPage({
            keyword: keyword.value,
            pageNo: pageNo.value,
            pageSize: pageSize.value
          })
        : LiveApi.getLiveAnchorPage({ status: 1, pageNo: pageNo.value, pageSize: pageSize.value }),
      LiveApi.getLiveCommissionList()
    ])
    if (sequence !== loadSequence) return
    const configured = new Map<number, number>(
      (rates as LiveApi.LiveCommissionRuleVO[])
        .filter((rate) => rate.targetType === targetType)
        .map((rate) => [rate.targetId, Number(rate.ratePercent)])
    )
    rows.value = (page.list ?? []).map((row) => {
      const targetId = targetType === 1 ? row.id : row.anchorUserId
      return {
        targetType,
        targetId,
        name: targetType === 1 ? row.name : row.anchorNickname || `主播 ${targetId}`,
        price: row.price,
        ratePercent: configured.get(targetId)
      }
    })
    total.value = page.total ?? 0
  } finally {
    if (sequence === loadSequence) loading.value = false
  }
}
const search = () => {
  pageNo.value = 1
  load()
}
const changeTab = () => {
  rows.value = []
  total.value = 0
  search()
}
const openForm = (row: CommissionRow) => {
  editing.value = row
  form.ratePercent = row.ratePercent
  visible.value = true
}
const save = async () => {
  if (saving.value || !editing.value) return
  await formRef.value?.validate()
  if (form.ratePercent == null) return
  saving.value = true
  try {
    await LiveApi.saveLiveCommission({
      targetType: editing.value.targetType,
      targetId: editing.value.targetId,
      ratePercent: form.ratePercent
    })
    message.success('比例已保存，对后续订单生效')
    visible.value = false
    await load()
  } finally {
    saving.value = false
  }
}
onMounted(load)
</script>
